import { useLayoutEffect, useState, type CSSProperties } from 'react'
import { useLocation } from 'react-router-dom'

const CANVAS_PATH = '/session/canvas'
const GAP_PX = 8
const MIN_RIGHT_PX = 12
/** Used until Excalidraw mounts its top-right island. */
const FALLBACK_TOP_PX = 52

function canvasFallbackPosition(): CSSProperties {
  return { top: `${FALLBACK_TOP_PX}px`, right: `${MIN_RIGHT_PX}px` }
}

const ANCHOR_SELECTORS = [
  '.excalidraw .layer-ui__wrapper__top-right',
  '.excalidraw .default-sidebar .sidebar-triggers',
  '.excalidraw .App-menu_top > *:last-child',
  '.excalidraw .sidebar-trigger',
]

function findLibraryAnchor(): HTMLElement | null {
  for (const selector of ANCHOR_SELECTORS) {
    const el = document.querySelector(selector)
    if (el instanceof HTMLElement) return el
  }
  return null
}

function measureChromePosition(): CSSProperties {
  const anchor = findLibraryAnchor()
  if (!anchor) {
    return {
      top: `${FALLBACK_TOP_PX}px`,
      right: `${MIN_RIGHT_PX}px`,
    }
  }

  const rect = anchor.getBoundingClientRect()
  return {
    top: `${Math.round(rect.bottom + GAP_PX)}px`,
    right: `${Math.round(Math.max(window.innerWidth - rect.right, MIN_RIGHT_PX))}px`,
  }
}

/** On the canvas route, position PageChrome below Excalidraw's library control. */
export function useCanvasChromePosition(): {
  isCanvas: boolean
  positionStyle: CSSProperties | undefined
} {
  const { pathname } = useLocation()
  const isCanvas = pathname === CANVAS_PATH
  const [positionStyle, setPositionStyle] = useState<CSSProperties | undefined>(() =>
    pathname === CANVAS_PATH ? canvasFallbackPosition() : undefined,
  )

  useLayoutEffect(() => {
    if (!isCanvas) {
      setPositionStyle(undefined)
      return
    }

    const update = () => setPositionStyle(measureChromePosition())

    update()
    const raf = requestAnimationFrame(update)
    const t1 = window.setTimeout(update, 250)
    const t2 = window.setTimeout(update, 900)

    const root = document.querySelector('.excalidraw')
    const observer = root
      ? new MutationObserver(update)
      : null
    if (root && observer) {
      observer.observe(root, { childList: true, subtree: true, attributes: true })
    }

    window.addEventListener('resize', update)
    return () => {
      cancelAnimationFrame(raf)
      window.clearTimeout(t1)
      window.clearTimeout(t2)
      observer?.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [isCanvas])

  return { isCanvas, positionStyle }
}
